// FILE: apps/admin/src/pages/AuthSessions.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT
//   PURPOSE: 3-tab Access & Roles («Доступы и роли») control center managing platform operators & RBAC role assignments via UserPicker, full 20-permission RBAC matrix comparison, and enriched active Admin sessions with UA parsing and instant revocation.
//   SCOPE: parseAdminUserAgent helper and AuthSessions component.
//   DEPENDS: @brainrotstore/domain, ../api.js, ../components/ConfirmDialog.js, ../components/Icons.js, ../components/UserAvatar.js, ../components/UserPicker.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   AuthSessions - 3-tab Access & Roles page (Operators & Roles, RBAC Permission Matrix, Active Admin Logins)
//   parseAdminUserAgent - Parse raw HTTP User-Agent string into human-readable Browser and OS summary
// END_MODULE_MAP

import {
  PLATFORM_PERMISSIONS,
  type PlatformPermission,
  type PlatformRole,
  ROLE_PERMISSION_MATRIX,
} from '@brainrotstore/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { api, dt } from '../api.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { AdminIcon } from '../components/Icons.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { type PickedAdminUser, UserPicker } from '../components/UserPicker.js';
import { useToast } from '../toast.js';

type AccessTab = 'operators' | 'matrix' | 'sessions';

interface OperatorRow {
  telegramId: number;
  userId: string | null;
  username: string | null;
  displayName: string | null;
  photoUrl: string | null;
  roles: PlatformRole[];
  isBootstrapSuperAdmin?: boolean;
  isBootstrapEnv?: boolean;
  activeSessionsCount?: number;
  lastSeenAt: string | null;
}

interface SessionRow {
  id: string;
  userId: string | null;
  telegramId: number;
  username: string | null;
  displayName?: string | null;
  photoUrl?: string | null;
  kind: 'ADMIN' | 'MINIAPP';
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string;
  lastSeenAt?: string | null;
  expiresAt: string;
  revokedAt: string | null;
}

interface AdminMeResponse {
  telegramId: number;
  sessionId: string;
  username: string | null;
  roles: PlatformRole[];
  permissions: PlatformPermission[];
}

const ROLE_OPTIONS: Array<{
  role: PlatformRole;
  title: string;
  chipClass: string;
  hint: string;
}> = [
  {
    role: 'superadministrator',
    title: 'Суперадмин',
    chipClass: 'DELIVERING',
    hint: 'Полный доступ ко всем настройкам, казне и выдаче ролей',
  },
  {
    role: 'administrator',
    title: 'Администратор',
    chipClass: 'info',
    hint: 'Заказы, платежи, цены, квесты, балансы, кроме управления ролями',
  },
  {
    role: 'support',
    title: 'Саппорт',
    chipClass: 'ok',
    hint: 'Просмотр админки, тикеты, апелляции банов, модерация профилей и уход за рыбками',
  },
];

const ROLE_META_MAP: Record<PlatformRole, (typeof ROLE_OPTIONS)[number]> = {
  superadministrator: ROLE_OPTIONS[0]!,
  administrator: ROLE_OPTIONS[1]!,
  support: ROLE_OPTIONS[2]!,
};

const PERMISSION_META: Record<
  PlatformPermission,
  { title: string; detail: string }
> = {
  'admin.read': {
    title: 'Просмотр дашборда и разделов',
    detail: 'Вход в панель управления, просмотр сводки, метрик и списков',
  },
  'order.manage': {
    title: 'Управление заказами',
    detail: 'Просмотр карточек заказов, повтор выдачи и отмена неоплаченных заказов',
  },
  'order.force_match': {
    title: 'Ручное зачисление оплаты заказа',
    detail: 'Принудительное подтверждение оплаты заказа с запуском доставки',
  },
  'payment.match': {
    title: 'Сопоставление платежей СБП',
    detail: 'Ручная привязка входящих переводов Т-Банка к заказам',
  },
  'admin.user.manage': {
    title: 'Управление профилями игроков',
    detail: 'Верификация, опыт XP/стрик, реферальный код и сброс ника/аватара',
  },
  'admin.user.suspend': {
    title: 'Блокировка и разбан игроков',
    detail: 'Выдача блокировок, снятие банов и рассмотрение апелляций',
  },
  'admin.tamagotchi.manage': {
    title: 'Уход за рыбками и коллекцией',
    detail: 'Кормление, лечение, воскрешение рыбки и управление компаньонами',
  },
  'admin.role.manage': {
    title: 'Управление ролями операторов',
    detail: 'Назначение и снятие ролей Суперадмина, Администратора и Саппорта',
  },
  'support.manage': {
    title: 'Тикеты поддержки и жалобы',
    detail: 'Ответы в обращениях, модерация жалоб на профили и сообщения от бота',
  },
  'broadcast.manage': {
    title: 'Массовые рассылки в боте',
    detail: 'Создание и запуск сегментированных рассылок по аудитории бота',
  },
  'banners.manage': {
    title: 'Управление промо-баннерами',
    detail: 'Добавление, редактирование и удаление баннеров на витрине Mini App',
  },
  'balance.admin_adjust': {
    title: 'Корректировка балансов RUB / GRAM',
    detail: 'Ручное начисление и списание средств через атомарный леджер',
  },
  'config.economy.update': {
    title: 'Настройка цен, наценок и комиссий',
    detail: 'Базовые множители Stars/Premium, ступени за оборот и персональные тарифы',
  },
  'config.referrals.update': {
    title: 'Настройка реферальной программы',
    detail: 'Поколения начислений, проценты вознаграждений и настраиваемые промокоды',
  },
  'treasury.read': {
    title: 'Доступ к казначейству и леджеру',
    detail: 'Просмотр резервов GRAM/TON, 21-дневного холда Stars, DRIFT-сверки и P&L',
  },
  'games.manage': {
    title: 'Управление мини-играми Crash и Кейсы',
    detail: 'Настройка House Edge, лимитов ставок и вероятностей дропа в кейсах',
  },
  'quests.manage': {
    title: 'Управление заданиями и квестами',
    detail: 'Создание квестов, редактирование целей и наград XP / RUB',
  },
  'fragment.session.manage': {
    title: 'Управление сессией Fragment',
    detail: 'Обновление зашифрованных cookies и проверка авторизации на fragment.com',
  },
  'tbank.session.manage': {
    title: 'Управление сессией Т-Банка',
    detail: 'Переподключение сессии мониторинга входящих платежей СБП',
  },
  'audit.read': {
    title: 'Чтение журнала аудита и сессий',
    detail: 'Просмотр истории привилегированных действий и активных входов в панель',
  },
};

// START_CONTRACT: parseAdminUserAgent
//   PURPOSE: Convert a raw HTTP User-Agent string into a concise human-readable Browser + OS label.
//   INPUTS: { ua: string | null | undefined }
//   OUTPUTS: { string }
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: parseAdminUserAgent
export function parseAdminUserAgent(ua: string | null | undefined): string {
  // START_BLOCK_PARSE_ADMIN_USER_AGENT
  const raw = ua?.trim();
  if (!raw) return 'Неизвестный клиент';

  if (/TelegramDesktop|tdesktop/i.test(raw)) return 'Telegram Desktop';
  if (/Telegram-Android/i.test(raw)) return 'Telegram · Android';
  if (/Telegram-iOS/i.test(raw)) return 'Telegram · iOS';

  let browser = 'Браузер';
  if (/YaBrowser\//i.test(raw)) browser = 'Яндекс Браузер';
  else if (/Edg\//i.test(raw)) browser = 'Edge';
  else if (/OPR\/|Opera/i.test(raw)) browser = 'Opera';
  else if (/Firefox\//i.test(raw)) browser = 'Firefox';
  else if (/CriOS\//i.test(raw)) browser = 'Chrome';
  else if (/Chrome\//i.test(raw)) browser = 'Chrome';
  else if (/Safari\//i.test(raw)) browser = 'Safari';
  else if (/curl\//i.test(raw)) browser = 'cURL';

  let os = '';
  if (/iPhone|iPad|iPod/i.test(raw)) os = 'iOS';
  else if (/Android/i.test(raw)) os = 'Android';
  else if (/Windows NT/i.test(raw)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(raw)) os = 'macOS';
  else if (/Linux/i.test(raw)) os = 'Linux';

  return os ? `${browser} · ${os}` : browser;
  // END_BLOCK_PARSE_ADMIN_USER_AGENT
}

interface PendingRoleConfirm {
  targetIdentifier: string;
  targetLabel: string;
  roles: PlatformRole[];
  danger: boolean;
}

// START_CONTRACT: AuthSessions
//   PURPOSE: Render the 3-tab Access & Roles («Доступы и роли») control plane for operators, RBAC matrix, and active Admin sessions.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries /admin/access/operators, /admin/auth-sessions, and /admin/me; executes POST /admin/users/:id/roles and POST /admin/auth-sessions/:id/revoke
//   LINKS: M-ADMIN
// END_CONTRACT: AuthSessions
export function AuthSessions() {
  // START_BLOCK_AUTH_SESSIONS
  const qc = useQueryClient();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<AccessTab>('operators');
  const [targetIdentifier, setTargetIdentifier] = useState('');
  const [pickedUser, setPickedUser] = useState<PickedAdminUser | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<PlatformRole[]>(['support']);
  const [pendingRoleConfirm, setPendingRoleConfirm] = useState<PendingRoleConfirm | null>(null);

  const { data: meData } = useQuery({
    queryKey: ['admin-me'],
    queryFn: () => api.get<AdminMeResponse>('/admin/me'),
    staleTime: 30_000,
  });

  const { data: operatorsRaw, isLoading: operatorsLoading } = useQuery({
    queryKey: ['admin-operators'],
    queryFn: () =>
      api.get<{ items: OperatorRow[] } | OperatorRow[]>('/admin/access/operators'),
    refetchInterval: 20_000,
  });

  const operators: OperatorRow[] = useMemo(
    () => (Array.isArray(operatorsRaw) ? operatorsRaw : (operatorsRaw?.items ?? [])),
    [operatorsRaw],
  );

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['admin-auth-sessions'],
    queryFn: () => api.get<{ items: SessionRow[] }>('/admin/auth-sessions'),
    refetchInterval: 15_000,
  });

  const sessions = useMemo(() => sessionsData?.items ?? [], [sessionsData]);

  const currentSessionId = useMemo(() => {
    if (!meData) return null;
    const exact = sessions.find((s) => s.id === meData.sessionId);
    if (exact) return exact.id;
    const newestOwnActive = sessions.find(
      (s) => s.telegramId === meData.telegramId && !s.revokedAt && s.kind === 'ADMIN',
    );
    return newestOwnActive?.id ?? null;
  }, [meData, sessions]);

  const revokeMut = useMutation({
    mutationFn: (id: string) =>
      api.post(`/admin/auth-sessions/${encodeURIComponent(id)}/revoke`, {
        reason: 'Принудительное завершение сессии оператором',
      }),
    onSuccess: () => {
      toast('Сессия завершена и отозвана');
      void qc.invalidateQueries({ queryKey: ['admin-auth-sessions'] });
      void qc.invalidateQueries({ queryKey: ['admin-operators'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка завершения сессии'),
  });

  const toggleRole = (role: PlatformRole) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const handleOpenGrantModal = () => {
    const idTrimmed = targetIdentifier.trim();
    if (!idTrimmed) return;
    const label = pickedUser
      ? `${pickedUser.customName || pickedUser.firstName || 'Пользователь'} (${pickedUser.username ? `@${pickedUser.username}` : `TG #${pickedUser.telegramId}`})`
      : idTrimmed;
    setPendingRoleConfirm({
      targetIdentifier: idTrimmed,
      targetLabel: label,
      roles: selectedRoles,
      danger: selectedRoles.length === 0,
    });
  };

  const handlePrefillOperator = (op: OperatorRow) => {
    const id = op.userId ?? String(op.telegramId);
    setTargetIdentifier(id);
    setPickedUser(null);
    setSelectedRoles(op.roles.length > 0 ? [...op.roles] : ['support']);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenRevokeAllRoles = (op: OperatorRow) => {
    const id = op.userId ?? String(op.telegramId);
    const label =
      op.displayName || (op.username ? `@${op.username}` : `TG #${op.telegramId}`);
    setPendingRoleConfirm({
      targetIdentifier: id,
      targetLabel: label,
      roles: [],
      danger: true,
    });
  };

  const handleConfirmRoleMutation = async ({
    reason,
    stepUpGrantToken,
  }: {
    reason: string;
    stepUpGrantToken?: string;
  }) => {
    if (!pendingRoleConfirm) return;
    await api.post(
      `/admin/users/${encodeURIComponent(pendingRoleConfirm.targetIdentifier)}/roles`,
      {
        roles: pendingRoleConfirm.roles,
        reason,
        stepUpGrantToken,
      },
    );
    const wasRevoke = pendingRoleConfirm.roles.length === 0;
    setPendingRoleConfirm(null);
    if (!wasRevoke) {
      setTargetIdentifier('');
      setPickedUser(null);
    }
    void qc.invalidateQueries({ queryKey: ['admin-operators'] });
    void qc.invalidateQueries({ queryKey: ['admin-me'] });
    toast(
      wasRevoke
        ? 'Назначенные роли оператора сняты'
        : 'Роли оператора успешно обновлены',
    );
  };

  const activeSessionsCount = sessions.filter((s) => !s.revokedAt).length;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Доступы и роли</h1>
          <div className="page-subtitle">
            Управление правами администраторов и саппорта, матрица полномочий RBAC и контроль активных входов в панель
          </div>
        </div>
      </div>

      {/* 3 Horizontal Tabs */}
      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'operators' ? 'active' : ''}`}
          onClick={() => setActiveTab('operators')}
        >
          Администраторы и роли ({operators.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'matrix' ? 'active' : ''}`}
          onClick={() => setActiveTab('matrix')}
        >
          Описание ролей и прав ({PLATFORM_PERMISSIONS.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Активные входы в админку ({activeSessionsCount})
        </button>
      </div>

      {/* Tab 1: Operators & Role Assignment */}
      {activeTab === 'operators' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Выдать или изменить роль пользователю</h2>
            <div className="grid cols-2" style={{ alignItems: 'start', gap: 18 }}>
              <div>
                <UserPicker
                  value={targetIdentifier}
                  onChange={(val, u) => {
                    setTargetIdentifier(val);
                    setPickedUser(u ?? null);
                  }}
                  label="Пользователь (выберите из списка или введите @username / Telegram ID) *"
                />
                <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                  Можно выбрать зарегистрированного игрока из выпадающего списка или ввести числовой
                  Telegram ID нового сотрудника поддержки напрямую.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label className="field-label" style={{ marginBottom: 0 }}>
                  Назначаемые роли RBAC
                </label>

                {ROLE_OPTIONS.map((opt) => {
                  const checked = selectedRoles.includes(opt.role);
                  return (
                    <label
                      key={opt.role}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: checked ? 'rgba(130, 148, 255, 0.12)' : 'var(--bg-2)',
                        border: checked
                          ? '1px solid rgba(130, 148, 255, 0.38)'
                          : '1px solid var(--hair)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(opt.role)}
                        style={{ marginTop: 2, width: 16, height: 16 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{opt.title}</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          {opt.hint}
                        </div>
                      </div>
                    </label>
                  );
                })}

                <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={!targetIdentifier.trim() || selectedRoles.length === 0}
                    onClick={handleOpenGrantModal}
                  >
                    <AdminIcon name="shield" size={14} />
                    <span>Назначить роли (Step-Up)</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--hair)' }}>
              <h2 style={{ margin: 0 }}>Действующие операторы платформы</h2>
            </div>

            {operatorsLoading ? (
              <div className="muted" style={{ padding: 24 }}>
                Загрузка списка операторов…
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Оператор</th>
                      <th>Назначенные роли</th>
                      <th>Активных сессий</th>
                      <th>Последний вход</th>
                      <th style={{ textAlign: 'right' }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operators.map((op) => {
                      const isBootstrap = Boolean(
                        op.isBootstrapEnv || op.isBootstrapSuperAdmin,
                      );
                      const primaryName =
                        op.displayName ||
                        (op.username ? `@${op.username}` : `Оператор #${op.telegramId}`);
                      return (
                        <tr key={op.telegramId}>
                          <td>
                            <div className="user-cell">
                              <UserAvatar
                                photoUrl={op.photoUrl}
                                name={primaryName}
                                size={34}
                              />
                              <div className="user-cell-meta">
                                <div style={{ fontWeight: 600 }}>{primaryName}</div>
                                <div className="muted mono" style={{ fontSize: 11.5 }}>
                                  {op.username ? `@${op.username} · ` : 'без username · '}TG #
                                  {op.telegramId}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="row" style={{ gap: 6 }}>
                              {op.roles.map((r) => {
                                const meta = ROLE_META_MAP[r];
                                return (
                                  <span
                                    key={r}
                                    className={`chip ${meta?.chipClass ?? 'info'}`}
                                  >
                                    {meta?.title ?? r}
                                  </span>
                                );
                              })}
                              {isBootstrap && (
                                <span
                                  className="chip AWAITING_PAYMENT mono"
                                  title="Назначен через переменную окружения OPERATOR_CHAT_IDS"
                                >
                                  ENV Bootstrap
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="mono">{op.activeSessionsCount ?? 0}</td>
                          <td className="muted mono">{dt(op.lastSeenAt)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <div
                              className="row"
                              style={{ justifyContent: 'flex-end', gap: 6 }}
                            >
                              <button
                                type="button"
                                className="btn ghost sm"
                                onClick={() => handlePrefillOperator(op)}
                              >
                                Изменить роли
                              </button>
                              {!isBootstrap && (
                                <button
                                  type="button"
                                  className="btn danger sm"
                                  onClick={() => handleOpenRevokeAllRoles(op)}
                                >
                                  Снять роли
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {operators.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="muted"
                          style={{ textAlign: 'center', padding: 28 }}
                        >
                          Операторы не найдены
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: RBAC Matrix Description */}
      {activeTab === 'matrix' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="grid cols-3">
            {ROLE_OPTIONS.map((opt) => (
              <div key={opt.role} className="card">
                <div
                  className="row"
                  style={{ justifyContent: 'space-between', marginBottom: 6 }}
                >
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{opt.title}</span>
                  <span className={`chip ${opt.chipClass} mono`}>
                    {ROLE_PERMISSION_MATRIX[opt.role].length} / {PLATFORM_PERMISSIONS.length}{' '}
                    прав
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  {opt.hint}
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Полномочие (RBAC)</th>
                    <th style={{ textAlign: 'center', width: 130 }}>Суперадмин</th>
                    <th style={{ textAlign: 'center', width: 140 }}>Администратор</th>
                    <th style={{ textAlign: 'center', width: 120 }}>Саппорт</th>
                  </tr>
                </thead>
                <tbody>
                  {PLATFORM_PERMISSIONS.map((perm) => {
                    const meta = PERMISSION_META[perm];
                    const hasSuper =
                      ROLE_PERMISSION_MATRIX.superadministrator.includes(perm);
                    const hasAdmin = ROLE_PERMISSION_MATRIX.administrator.includes(perm);
                    const hasSupport = ROLE_PERMISSION_MATRIX.support.includes(perm);

                    const renderCell = (granted: boolean) =>
                      granted ? (
                        <span className="chip ok" style={{ padding: '3px 8px' }}>
                          <AdminIcon name="check" size={13} />
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      );

                    return (
                      <tr key={perm}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{meta.title}</div>
                          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                            {meta.detail} · <code className="mono">{perm}</code>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>{renderCell(hasSuper)}</td>
                        <td style={{ textAlign: 'center' }}>{renderCell(hasAdmin)}</td>
                        <td style={{ textAlign: 'center' }}>{renderCell(hasSupport)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Active Admin Sessions */}
      {activeTab === 'sessions' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {sessionsLoading ? (
            <div className="muted" style={{ padding: 24 }}>
              Загрузка активных входов в панель…
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Оператор</th>
                    <th>Браузер и ОС</th>
                    <th>IP-адрес</th>
                    <th>Вход выполнен</th>
                    <th>Последняя активность</th>
                    <th>Статус</th>
                    <th style={{ textAlign: 'right' }}>Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => {
                    const isCurrent = currentSessionId === s.id;
                    const nameLabel =
                      s.displayName ||
                      (s.username ? `@${s.username}` : `TG #${s.telegramId}`);
                    const lastActiveIso = s.lastSeenAt ?? s.lastUsedAt;

                    return (
                      <tr key={s.id}>
                        <td>
                          <div className="user-cell">
                            <UserAvatar
                              photoUrl={s.photoUrl ?? null}
                              name={nameLabel}
                              size={32}
                            />
                            <div className="user-cell-meta">
                              <div className="user-cell-name-row">
                                <span style={{ fontWeight: 600 }}>{nameLabel}</span>
                                {isCurrent && (
                                  <span className="chip info" style={{ padding: '2px 7px' }}>
                                    Текущая сессия
                                  </span>
                                )}
                              </div>
                              <div className="muted mono" style={{ fontSize: 11.5 }}>
                                {s.username ? `@${s.username} · ` : ''}TG #{s.telegramId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td title={s.userAgent ?? undefined}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                            <AdminIcon name="device" size={14} />
                            <span>{parseAdminUserAgent(s.userAgent)}</span>
                          </div>
                        </td>
                        <td className="mono">{s.ipAddress ?? '—'}</td>
                        <td className="muted mono">{dt(s.createdAt)}</td>
                        <td className="muted mono">{dt(lastActiveIso)}</td>
                        <td>
                          <span className={`chip ${s.revokedAt ? 'CANCELLED' : 'ok'}`}>
                            {s.revokedAt ? 'ОТОЗВАНА' : 'АКТИВНА'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {!s.revokedAt && (
                            <button
                              type="button"
                              className="btn sm danger"
                              disabled={revokeMut.isPending}
                              onClick={() => revokeMut.mutate(s.id)}
                            >
                              <AdminIcon name="logout" size={13} />
                              <span>Завершить сессию</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {sessions.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="muted"
                        style={{ textAlign: 'center', padding: 28 }}
                      >
                        Активных сессий в реестре нет
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingRoleConfirm !== null}
        title={
          pendingRoleConfirm?.roles.length === 0
            ? 'Снятие ролей оператора'
            : 'Назначение ролей оператора'
        }
        description={
          pendingRoleConfirm
            ? pendingRoleConfirm.roles.length === 0
              ? `Все назначенные в БД роли будут сняты у оператора: ${pendingRoleConfirm.targetLabel}`
              : `Оператор: ${pendingRoleConfirm.targetLabel} · Роли: ${pendingRoleConfirm.roles
                  .map((r) => ROLE_META_MAP[r]?.title ?? r)
                  .join(', ')}`
            : undefined
        }
        confirmLabel={
          pendingRoleConfirm?.roles.length === 0 ? 'Снять роли' : 'Сохранить роли'
        }
        danger={Boolean(pendingRoleConfirm?.danger)}
        stepUpAction="user.role.set"
        onCancel={() => setPendingRoleConfirm(null)}
        onConfirm={handleConfirmRoleMutation}
      />
    </>
  );
  // END_BLOCK_AUTH_SESSIONS
}
